// assets/routine-finder.js
// EviDenS Routine Finder — single ESM module loaded by sections/routine-finder.liquid.
// Exports pure functions/classes for unit tests; registers custom elements at the bottom
// only when running in a browser. Never uses innerHTML for dynamic content.

/**
 * Build a DOM element. Children that are strings become text nodes (no HTML parsing);
 * children that are nodes are appended; null/undefined children are skipped.
 *
 * @param {string} tag
 * @param {Record<string, any>|null} attrs
 * @param  {...(Node|string|null|undefined)} children
 * @returns {HTMLElement}
 */
export function el(tag, attrs, ...children) {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value == null || value === false) continue;
      if (key.startsWith('on') && typeof value === 'function') {
        node.addEventListener(key.slice(2).toLowerCase(), value);
      } else if (key === 'class' || key === 'className') {
        node.className = String(value);
      } else if (key === 'hidden') {
        if (value) node.hidden = true;
      } else {
        node.setAttribute(key, String(value));
      }
    }
  }
  for (const child of children) {
    if (child == null) continue;
    if (typeof child === 'string' || typeof child === 'number') {
      node.appendChild(document.createTextNode(String(child)));
    } else if (child instanceof Node) {
      node.appendChild(child);
    }
  }
  return node;
}

// ── Engine ─────────────────────────────────────────────────────────────────

export class EngineMissingKeyError extends Error {
  constructor(key) {
    super(`Routine finder: no recommendation for key "${key}"`);
    this.name = 'EngineMissingKeyError';
    this.key = key;
  }
}

// Applies per-market product swaps and whole-step removals to the flat slot list.
function applyMarketOverrides(slots, eyeAddon, overrides, marketRaw) {
  const market = String(marketRaw).toLowerCase();
  const rows = overrides.filter((o) => o && String(o.market || '').toLowerCase() === market);
  if (!rows.length) return { slots, eyeAddon };

  const removeSteps = new Set();
  const swapByHandle = {};
  for (const o of rows) {
    const stepNum = o.step != null && o.step !== '' ? parseInt(o.step, 10) : null;
    const hasRepl = o.replacement && o.replacement.product_id;
    if (!hasRepl && stepNum != null && !Number.isNaN(stepNum)) { removeSteps.add(stepNum); continue; }
    if (hasRepl && o.original_handle) swapByHandle[o.original_handle] = o.replacement;
  }

  const nextSlots = slots
    .filter((s) => !removeSteps.has(s.step))
    .map((s) => (swapByHandle[s.handle] ? { ...s, ...swapByHandle[s.handle] } : s));

  let nextEye = eyeAddon;
  if (nextEye && swapByHandle[nextEye.handle]) nextEye = { ...nextEye, ...swapByHandle[nextEye.handle] };
  return { slots: nextSlots, eyeAddon: nextEye };
}

/**
 * Pure recommendation lookup. Two map lookups, no fallback.
 *
 * @param {object} answers
 * @param {object} data  { recommendations, eye, market_overrides? }
 * @param {{market?: string}} [opts]  Active Shopify market handle — applies overrides if set
 */
export function recommend(answers, data, opts = {}) {
  const recKey = `${answers.age}:${answers.skin}:${answers.benefit}`;
  const rec = data.recommendations[recKey];
  if (!rec) throw new EngineMissingKeyError(recKey);

  const eyeKey = `${answers.age}:${answers.eye}`;
  const eye = data.eye[eyeKey];
  if (!eye) throw new EngineMissingKeyError(eyeKey);

  let slots = rec.steps.slice();
  let eyeAddon = eye;

  if (opts.market && Array.isArray(data.market_overrides) && data.market_overrides.length) {
    ({ slots, eyeAddon } = applyMarketOverrides(slots, eyeAddon, data.market_overrides, opts.market));
  }

  const groups = [];
  const byStep = new Map();
  for (const s of slots) {
    if (!byStep.has(s.step)) {
      const g = { step: s.step, products: [] };
      byStep.set(s.step, g);
      groups.push(g);
    }
    byStep.get(s.step).products.push(s);
  }
  groups.sort((a, b) => a.step - b.step);
  return { steps: groups, eyeAddon };
}

// ── URL token ──────────────────────────────────────────────────────────────

const REQUIRED_ANSWER_KEYS = ['age', 'skin', 'benefit', 'eye', 'ritual'];

export function encodeToken(answers, swaps) {
  const payload = { ...answers };
  if (swaps) {
    const sw = {};
    for (const [slot, p] of Object.entries(swaps)) {
      if (p && p.handle && p.product_id) sw[slot] = { h: p.handle, p: p.product_id };
    }
    if (Object.keys(sw).length) payload.sw = sw;
  }
  const json = JSON.stringify(payload);
  const b64 = typeof btoa === 'function'
    ? btoa(unescape(encodeURIComponent(json)))
    : Buffer.from(json, 'utf-8').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeToken(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const padded = token.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(token.length / 4) * 4, '=');
    const json = typeof atob === 'function'
      ? decodeURIComponent(escape(atob(padded)))
      : Buffer.from(padded, 'base64').toString('utf-8');
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    for (const k of REQUIRED_ANSWER_KEYS) {
      if (typeof parsed[k] !== 'string') return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// ── State machine ──────────────────────────────────────────────────────────

const ANSWER_KEYS = ['age', 'ritual', 'skin', 'benefit', 'eye'];

export class RoutineState {
  constructor(initial = {}) {
    this.answers = { ...(initial.answers || {}) };
    this.recommendation = initial.recommendation || null;
    this.swaps = { ...(initial.swaps || {}) };
    this.savedAt = initial.savedAt || null;
  }

  setAnswer(key, value) {
    if (!ANSWER_KEYS.includes(key)) throw new Error(`RoutineState.setAnswer: unknown key "${key}"`);
    this.answers[key] = value;
  }

  isComplete() {
    return ANSWER_KEYS.every((k) => typeof this.answers[k] === 'string' && this.answers[k].length);
  }

  computeRecommendation(data, opts = {}) {
    if (!this.isComplete()) throw new Error('RoutineState: cannot compute before all answers are set');
    this.recommendation = recommend(this.answers, data, opts);
    return this.recommendation;
  }

  swapSlot(slot, product) {
    if (!product || !product.product_id) throw new Error('swapSlot: product.product_id required');
    this.swaps[slot] = product;
  }

  clearSwap(slot) {
    delete this.swaps[slot];
  }

  effectiveSlotProduct(slot) {
    if (this.swaps[slot]) return this.swaps[slot];
    if (!this.recommendation) return null;
    for (const g of this.recommendation.steps) {
      const found = g.products.find((p) => p.slot === slot);
      if (found) return found;
    }
    return null;
  }

  effectiveEyeProduct() {
    return this.recommendation ? this.recommendation.eyeAddon : null;
  }

  clear() {
    this.answers = {};
    this.recommendation = null;
    this.swaps = {};
    this.savedAt = null;
  }

  toJSON() {
    const slots = [];
    if (this.recommendation) {
      for (const g of this.recommendation.steps) {
        for (const p of g.products) {
          const eff = this.swaps[p.slot] || p;
          slots.push({ step: g.step, slot: p.slot, product_id: eff.product_id, handle: eff.handle });
        }
      }
    }
    return {
      version: 2,
      saved_at: this.savedAt,
      answers: { ...this.answers },
      steps: slots,
      eye_addon: this.recommendation && this.recommendation.eyeAddon
        ? { product_id: this.recommendation.eyeAddon.product_id, handle: this.recommendation.eyeAddon.handle }
        : null,
    };
  }

  static fromJSON(json) {
    if (!json || typeof json !== 'object') return new RoutineState();
    return new RoutineState({ answers: json.answers, savedAt: json.saved_at || null });
  }
}

// ── localStorage persistence ───────────────────────────────────────────────

export const STORAGE_KEY = 'evidens:routine:v1';

function defaultStorage() {
  return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
}

export function loadFromLocalStorage(storage = defaultStorage()) {
  if (!storage) return null;
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return RoutineState.fromJSON(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveToLocalStorage(state, storage = defaultStorage()) {
  if (!storage) return;
  storage.setItem(STORAGE_KEY, JSON.stringify(state.toJSON()));
}

export function clearLocalStorage(storage = defaultStorage()) {
  if (!storage) return;
  storage.removeItem(STORAGE_KEY);
}

// ── Save flow ──────────────────────────────────────────────────────────────

const KLAVIYO_BASE = 'https://a.klaviyo.com/client';
const KLAVIYO_REVISION = '2024-10-15';

async function fetchWithRetry(url, init, retries = 1) {
  let lastResponse;
  for (let attempt = 0; attempt <= retries; attempt++) {
    lastResponse = await fetch(url, init);
    if (lastResponse.ok || lastResponse.status < 500) return lastResponse;
  }
  return lastResponse;
}

function buildKlaviyoProperties(input) {
  const handles = (input.steps || []).map((s) => s.handle).filter(Boolean);
  const skus = (input.steps || []).map((s) => s.sku).filter(Boolean);
  if (input.eyeAddon && input.eyeAddon.handle) handles.push(input.eyeAddon.handle);
  if (input.eyeAddon && input.eyeAddon.sku) skus.push(input.eyeAddon.sku);
  return {
    'Routine Last Quiz Date': new Date().toISOString(),
    'Routine Age': input.answers.age,
    'Routine Skin': input.answers.skin,
    'Routine Benefit': input.answers.benefit,
    'Routine Eye Objective': input.answers.eye,
    'Routine Ritual': input.answers.ritual,
    'Routine Step Products': handles,
    'Routine SKUs': skus,
    'Routine Opt In': !!input.optIn,
    'Routine URL': input.routineUrl,
  };
}

export async function saveRoutine(input) {
  if (!input || !input.email) throw new Error('saveRoutine: email required');
  if (!input.klaviyoCompanyId) throw new Error('saveRoutine: klaviyoCompanyId required');

  const properties = buildKlaviyoProperties(input);

  // Note: Klaviyo's /client/profiles endpoint rejects a `subscriptions` field.
  // Marketing consent (when optIn) must go through /client/subscriptions with a list id;
  // here we record the opt-in as a profile property and leave subscribing to a follow-up.
  const profileBody = {
    data: {
      type: 'profile',
      attributes: {
        email: input.email,
        properties,
      },
    },
  };
  const headers = { 'Content-Type': 'application/json', accept: 'application/json', revision: KLAVIYO_REVISION };

  const profileResp = await fetchWithRetry(
    `${KLAVIYO_BASE}/profiles/?company_id=${encodeURIComponent(input.klaviyoCompanyId)}`,
    { method: 'POST', headers, body: JSON.stringify(profileBody) }
  );

  if (!profileResp.ok) {
    let detail = ''; try { detail = await profileResp.text(); } catch {}
    console.error('[routine-finder] Klaviyo profile rejected', profileResp.status, detail);
    return { klaviyo: 'error', customerAccount: 'skipped' };
  }

  const eventBody = {
    data: {
      type: 'event',
      attributes: {
        properties,
        metric: { data: { type: 'metric', attributes: { name: 'Routine Saved' } } },
        profile: { data: { type: 'profile', attributes: { email: input.email } } },
      },
    },
  };
  const eventResp = await fetchWithRetry(
    `${KLAVIYO_BASE}/events/?company_id=${encodeURIComponent(input.klaviyoCompanyId)}`,
    { method: 'POST', headers, body: JSON.stringify(eventBody) }
  );

  const klaviyoStatus = eventResp.ok ? 'ok' : 'error';
  if (!eventResp.ok) {
    let detail = ''; try { detail = await eventResp.text(); } catch {}
    console.error('[routine-finder] Klaviyo event rejected', eventResp.status, detail);
  }

  // Opt-in marketing subscription (client-side) — requires a Klaviyo list id.
  // Klaviyo's profiles endpoint rejects consent, so subscribing goes through /client/subscriptions.
  if (input.optIn && input.klaviyoListId) {
    const subBody = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: input.email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
          custom_source: 'Routine Finder',
        },
        relationships: { list: { data: { type: 'list', id: input.klaviyoListId } } },
      },
    };
    const subResp = await fetchWithRetry(
      `${KLAVIYO_BASE}/subscriptions/?company_id=${encodeURIComponent(input.klaviyoCompanyId)}`,
      { method: 'POST', headers, body: JSON.stringify(subBody) }
    );
    if (!subResp.ok) {
      let detail = ''; try { detail = await subResp.text(); } catch {}
      console.error('[routine-finder] Klaviyo subscription rejected', subResp.status, detail);
    }
  }

  let customerAccount = 'skipped';
  if (input.customerAccessToken) {
    customerAccount = await writeCustomerMetafield(input).catch(() => 'error');
  }

  return { klaviyo: klaviyoStatus, customerAccount };
}

// Phase 2 hook — replace body with a real Customer Account API mutation when access-token plumbing is in place.
async function writeCustomerMetafield(_input) {
  return 'skipped';
}

// ── Storefront helpers ─────────────────────────────────────────────────────

const STOREFRONT_API_VERSION = '2024-10';

async function storefrontQuery(query, variables, opts) {
  const url = `https://${opts.shopDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Shopify-Storefront-Access-Token': opts.storefrontToken,
      'Accept-Language': opts.locale || 'en',
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!resp.ok) return null;
  const json = await resp.json();
  return json && json.data ? json.data : null;
}

function buildContextVariables(opts) {
  const vars = {};
  if (opts.country) vars.country = String(opts.country).toUpperCase();
  if (opts.language) vars.language = String(opts.language).toUpperCase();
  return vars;
}

const ALTERNATES_QUERY = `
  query Alternates($q: String!, $first: Int!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    products(query: $q, first: $first) {
      nodes {
        id handle title availableForSale
        featuredImage { url altText }
        priceRange { minVariantPrice { amount currencyCode } }
        step: metafield(namespace: "saho", key: "step") { value }
      }
    }
  }
`;

// The Storefront `products(query:)` search silently ignores metafield filters
// (a `metafield.saho.step:N` term returns the whole catalog, so every step showed
// the same pool). Instead we fetch the sellable catalog via `tag:nosample` — which
// also excludes sample/discovery SKUs the brand doesn't sell — and filter to the
// requested step in JS using each product's saho.step metafield. The whole sellable
// catalog is well under one page (≈70 products), so a single `first: 250` covers it.
export async function fetchAlternatesByStep(stepNumber, opts) {
  const data = await storefrontQuery(
    ALTERNATES_QUERY,
    { q: 'tag:nosample', first: 250, ...buildContextVariables(opts) },
    opts
  );
  const nodes = data && data.products ? data.products.nodes : [];
  const want = String(stepNumber);
  return nodes.filter((n) => n.step && String(n.step.value).trim() === want);
}

const PRODUCT_BY_HANDLE_QUERY = `
  query ProductByHandle($handle: String!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    productByHandle(handle: $handle) {
      id handle title
      shortDescription: metafield(namespace: "custom", key: "short_description") { value }
      featuredImage { url altText }
      priceRange { minVariantPrice { amount currencyCode } }
      variants(first: 20) {
        nodes { id title availableForSale price { amount currencyCode } }
      }
      sellingPlanGroups(first: 5) {
        nodes { name sellingPlans(first: 5) { nodes { id name } } }
      }
    }
  }
`;

// Shopify rich-text metafields return a JSON document, not a string. Flatten it
// to plain text (paragraphs joined by newlines). Plain-text metafields pass through.
export function richTextToPlain(raw) {
  if (raw == null) return '';
  let doc;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s[0] !== '{' && s[0] !== '[') return s;   // already plain text
    try { doc = JSON.parse(s); } catch { return s; }
  } else {
    doc = raw;
  }
  const paragraphs = [];
  const walk = (node, buf) => {
    if (!node) return;
    if (typeof node.value === 'string') buf.push(node.value);
    if (Array.isArray(node.children)) {
      if (node.type === 'paragraph' || node.type === 'list-item' || node.type === 'heading') {
        const local = [];
        node.children.forEach((c) => walk(c, local));
        paragraphs.push(local.join('').trim());
      } else {
        node.children.forEach((c) => walk(c, buf));
      }
    }
  };
  walk(doc, []);
  return paragraphs.filter(Boolean).join('\n').trim();
}

export async function fetchProductByHandle(handle, opts) {
  const data = await storefrontQuery(
    PRODUCT_BY_HANDLE_QUERY,
    { handle, ...buildContextVariables(opts) },
    opts
  );
  if (!data || !data.productByHandle) return null;
  const p = data.productByHandle;
  return {
    id: p.id,
    handle: p.handle,
    title: p.title,
    shortDescription: p.shortDescription ? richTextToPlain(p.shortDescription.value) : null,
    image: p.featuredImage,
    price: p.priceRange.minVariantPrice,
    variants: p.variants ? p.variants.nodes : [],
    sellingPlans: p.sellingPlanGroups
      ? p.sellingPlanGroups.nodes.flatMap((g) => g.sellingPlans.nodes)
      : [],
  };
}

// ── Browser-side helpers ───────────────────────────────────────────────────

let _i18nCache = null;

// Shopify's `t` filter HTML-escapes translations (' -> &#39;, & -> &amp;, etc.).
// We render every string through textContent, so decode the common entities back.
function decodeEntities(s) {
  return String(s)
    .replace(/&#0*39;/g, "'").replace(/&#x0*27;/gi, "'").replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"').replace(/&#0*34;/g, '"')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

export function t(key) {
  if (typeof document === 'undefined') return key;
  if (_i18nCache === null) {
    const node = document.getElementById('rf-i18n');
    _i18nCache = {};
    if (node) {
      try {
        const raw = JSON.parse(node.textContent);
        for (const [k, v] of Object.entries(raw)) _i18nCache[k] = typeof v === 'string' ? decodeEntities(v) : v;
      } catch { _i18nCache = {}; }
    }
  }
  return _i18nCache[key] || key;
}

// Localized string with an English fallback when the key is absent or unresolved.
export function tx(key, fallback) {
  const v = t(key);
  if (!v || v === key || (typeof v === 'string' && v.startsWith('Translation missing'))) return fallback;
  return v;
}

function readData() {
  if (typeof document === 'undefined') return null;
  const node = document.getElementById('rf-data');
  if (!node) return null;
  try { return JSON.parse(node.textContent); } catch { return null; }
}

function readSavedRoutine() {
  if (typeof document === 'undefined') return null;
  const node = document.getElementById('rf-saved-routine');
  if (!node) return null;
  try { return JSON.parse(node.textContent); } catch { return null; }
}

function getQueryParam(name) {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

function emitDataLayer(event, payload = {}) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });
}

function emitKlaviyoEvent(name, properties, companyId) {
  if (typeof window === 'undefined' || !companyId) return;
  fetch(`${KLAVIYO_BASE}/events/?company_id=${encodeURIComponent(companyId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json', revision: KLAVIYO_REVISION },
    body: JSON.stringify({
      data: {
        type: 'event',
        attributes: {
          properties,
          metric: { data: { type: 'metric', attributes: { name } } },
          profile: { data: { type: 'profile', attributes: { anonymous_id: properties.anonymous_id || `rf-${Date.now()}` } } },
        },
      },
    }),
  }).catch(() => {});
}

function formatMoney(price) {
  if (!price) return '';
  const formatter = new Intl.NumberFormat('en', { style: 'currency', currency: price.currencyCode || 'EUR' });
  return formatter.format(parseFloat(price.amount));
}

// ── <routine-finder> root component ────────────────────────────────────────

class RoutineFinderRoot extends HTMLElement {
  constructor() {
    super();
    this.state = new RoutineState();
    this.data = null;
    this._lastEmittedQuestion = null;
    this._startEmitted = false;
  }

  connectedCallback() {
    this.removeAttribute('hidden');
    this.data = readData() || { recommendations: {}, eye: {}, steps: [] };

    this.locale = this.dataset.locale || 'en';
    this.country = this.dataset.country || null;
    this.market = this.dataset.market || null;
    this.shopDomain = this.dataset.shopDomain;
    this.storefrontToken = this.dataset.storefrontToken;
    this.klaviyoCompanyId = this.dataset.klaviyoCompanyId;
    this.klaviyoListId = this.dataset.klaviyoListId || null;
    this.customerId = this.dataset.customerId || null;
    this.halfRitualSteps = (this.dataset.halfRitualSteps || '2,3,5').split(',').map((n) => parseInt(n, 10));

    this._initState();
    this._bindGlobalControls();
    this.addEventListener('rf:answer', (e) => this._onAnswer(e.detail));
    this.addEventListener('rf:restart', () => this._restart());
    window.addEventListener('hashchange', () => this._renderForHash());
    this._renderForHash();
  }

  _initState() {
    const token = getQueryParam('routine');
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) {
        const { sw, ...tokenAnswers } = decoded;
        Object.entries(tokenAnswers).forEach(([k, v]) => { if (ANSWER_KEYS.includes(k)) this.state.setAnswer(k, v); });
        if (sw && typeof sw === 'object') {
          for (const [slot, p] of Object.entries(sw)) {
            if (p && p.h && p.p) this.state.swapSlot(slot, { product_id: p.p, handle: p.h, slot });
          }
        }
        this._tryComputeAndJumpToResult('url_token');
        return;
      }
    }

    const saved = readSavedRoutine();
    if (saved && saved.answers) {
      Object.entries(saved.answers).forEach(([k, v]) => this.state.setAnswer(k, v));
      this.state.savedAt = saved.saved_at || null;
      this._tryComputeAndJumpToResult('metafield');
      return;
    }

    const localState = loadFromLocalStorage();
    if (localState && localState.isComplete()) {
      this.state = localState;
      this._tryComputeAndJumpToResult('localstorage');
      return;
    }
    if (localState) this.state = localState;

    if (!window.location.hash) window.location.hash = '#q1';
  }

  _tryComputeAndJumpToResult(source) {
    try {
      this.state.computeRecommendation(this.data, { market: this.market });
      window.location.hash = '#result';
      emitDataLayer('rf_routine_loaded_from_save', { source });
      emitKlaviyoEvent('Routine Loaded From Save', { source }, this.klaviyoCompanyId);
    } catch (e) {
      console.error('[routine-finder] could not restore', e);
      window.location.hash = '#q1';
    }
  }

  _bindGlobalControls() {
    const back = this.querySelector('[data-rf-back]');
    if (back) back.addEventListener('click', () => window.history.back());
    const restart = this.querySelector('[data-rf-restart]');
    if (restart) restart.addEventListener('click', () => this._restart());
  }

  _onAnswer({ questionId, questionKey, valueKey }) {
    this.state.setAnswer(questionKey, valueKey);
    saveToLocalStorage(this.state);

    if (this._lastEmittedQuestion !== questionId) {
      emitDataLayer('rf_question_answered', { question: questionId, answer_key: valueKey });
      emitKlaviyoEvent('Quiz Question Answered', { question: questionId, answer_key: valueKey }, this.klaviyoCompanyId);
      this._lastEmittedQuestion = questionId;
    }

    if (this.state.isComplete()) {
      try {
        this.state.computeRecommendation(this.data, { market: this.market });
        emitDataLayer('rf_quiz_completed', { ...this.state.answers });
        emitKlaviyoEvent('Quiz Completed', { ...this.state.answers }, this.klaviyoCompanyId);
        window.location.hash = '#result';
      } catch (e) {
        console.error('[routine-finder] engine error', e);
        const errorEl = this.querySelector('[data-rf-error]');
        if (errorEl) errorEl.hidden = false;
      }
    } else {
      const order = ['q1', 'q2', 'q3', 'q4', 'q5'];
      const idx = order.indexOf(questionId);
      const next = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : 'result';
      window.location.hash = `#${next}`;
    }
  }

  _renderForHash() {
    const hash = window.location.hash.replace('#', '') || 'q1';
    const allQuestions = this.querySelectorAll('routine-question');
    const result = this.querySelector('[data-rf-result]');
    const errorEl = this.querySelector('[data-rf-error]');

    allQuestions.forEach((q) => { q.hidden = q.dataset.questionId !== hash; });
    if (errorEl) errorEl.hidden = true;

    if (hash === 'result') {
      if (!this.state.isComplete() || !this.state.recommendation) {
        window.location.hash = '#q1';
        return;
      }
      result.hidden = false;
      result.dispatchEvent(new CustomEvent('rf:render-result', {
        detail: { state: this.state, halfRitualSteps: this.halfRitualSteps },
      }));
      this._toggleHeader('result');
    } else {
      result.hidden = true;
      this._toggleHeader('quiz');
      const order = ['q1', 'q2', 'q3', 'q4', 'q5'];
      const idx = order.indexOf(hash);
      if (idx >= 0) this._setProgress(idx + 1, 5);
      if (idx === 0 && !this._startEmitted) {
        emitDataLayer('rf_quiz_started');
        emitKlaviyoEvent('Quiz Started', {}, this.klaviyoCompanyId);
        this._startEmitted = true;
      }
    }
  }

  _toggleHeader(mode) {
    const isResult = mode === 'result';
    const header = this.querySelector('.rf__header');
    const back = this.querySelector('[data-rf-back]');
    const restart = this.querySelector('[data-rf-restart]');
    const progress = this.querySelector('[data-rf-progress]');
    // On the result, <routine-result> renders its own "Restart · View answers"
    // row, so hide the quiz header entirely to avoid a duplicate Restart.
    if (header) header.hidden = isResult;
    if (back) back.hidden = isResult;
    if (restart) restart.hidden = true;
    if (progress) progress.hidden = isResult;
  }

  _setProgress(current, total) {
    const c = this.querySelector('[data-rf-progress-current]');
    const tn = this.querySelector('[data-rf-progress-total]');
    if (c) c.textContent = String(current);
    if (tn) tn.textContent = String(total);
    // drives the gold progress-track fill in CSS (scaleX 0–1)
    this.style.setProperty('--rf-progress', total ? current / total : 0);
  }

  _restart() {
    this.state.clear();
    clearLocalStorage();
    this._lastEmittedQuestion = null;
    this._startEmitted = false;
    window.location.hash = '#q1';
  }
}

// ── <routine-question> ─────────────────────────────────────────────────────

class RoutineQuestion extends HTMLElement {
  connectedCallback() {
    this.questionId = this.dataset.questionId;
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        const btn = this.querySelector('[data-rf-continue]');
        if (btn) btn.disabled = false;
        this.dataset.selectedQuestionKey = input.dataset.questionKey;
        this.dataset.selectedValueKey = input.dataset.valueKey;
      });
    });

    const continueBtn = this.querySelector('[data-rf-continue]');
    if (continueBtn) continueBtn.addEventListener('click', () => {
      const qk = this.dataset.selectedQuestionKey;
      const vk = this.dataset.selectedValueKey;
      if (!qk || !vk) return;
      this.dispatchEvent(new CustomEvent('rf:answer', {
        bubbles: true,
        detail: { questionId: this.questionId, questionKey: qk, valueKey: vk },
      }));
    });

    this.querySelectorAll('[data-rf-tooltip-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const tooltip = trigger.parentElement.querySelector('.rf__option-tooltip');
        if (!tooltip) return;
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        tooltip.hidden = expanded;
        trigger.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }
}

// ── <routine-step-card> ────────────────────────────────────────────────────

class RoutineStepCard extends HTMLElement {
  async connectedCallback() {
    if (this._hydrated) return;
    this._hydrated = true;
    this._root = this.closest('routine-finder');
    if (!this._root || !this.dataset.handle) return;
    await this._render();
  }

  async _render() {
    while (this.firstChild) this.removeChild(this.firstChild);

    const stepNumber = this.dataset.step;
    const isEye = stepNumber === 'eye';
    const badgeText = isEye
      ? t('routine_finder.step.eye.badge')
      : t('routine_finder.step.number').replace('{number}', stepNumber);
    const labelText = isEye
      ? t('routine_finder.step.eye.label')
      : t(`routine_finder.step.${stepNumber}.label`);

    this.appendChild(el('div', { class: 'rf__step-badge' }, badgeText));
    this.appendChild(el('h3', { class: 'rf__step-label' }, labelText));

    const product = await fetchProductByHandle(this.dataset.handle, {
      shopDomain: this._root.shopDomain,
      storefrontToken: this._root.storefrontToken,
      locale: this._root.locale,
      country: this._root.country,
      language: this._root.locale,
    });

    const productHost = el('div', { class: 'rf__step-product', 'data-rf-step-product': '' });
    this.appendChild(productHost);

    if (!product) {
      productHost.appendChild(document.createTextNode('—'));
      return;
    }

    const variant = product.variants.find((v) => v.availableForSale) || product.variants[0];
    if (variant) this.dataset.variantId = variant.id;

    const imageLink = el('a', { class: 'rf__step-image', href: `/products/${product.handle}` });
    if (product.image && product.image.url) {
      const img = el('img', {
        class: 'rf__step-img',
        loading: 'lazy',
        alt: product.image.altText || product.title,
      });
      img.src = product.image.url;
      imageLink.appendChild(img);
    }
    productHost.appendChild(imageLink);

    const meta = el('div', { class: 'rf__step-meta' });
    meta.appendChild(el('a', { class: 'rf__step-title', href: `/products/${product.handle}` }, product.title));
    if (product.shortDescription) {
      meta.appendChild(el('p', { class: 'rf__step-desc' }, product.shortDescription));
    }
    const priceEl = el('span', { class: 'rf__step-price', 'data-rf-step-price': '' }, '');
    meta.appendChild(priceEl);

    // Track the displayed price so the result can total the routine; re-emit on change.
    const applyPrice = (priceObj) => {
      if (!priceObj) return;
      priceEl.textContent = formatMoney(priceObj);
      this.dataset.priceAmount = String(priceObj.amount);
      this.dataset.priceCurrency = priceObj.currencyCode || 'EUR';
      this.dispatchEvent(new CustomEvent('rf:price-change', { bubbles: true }));
    };
    applyPrice((variant && variant.price) ? variant.price : product.price);

    if (product.variants.length > 1) {
      const select = el('select', { class: 'rf__variant-select', 'data-rf-variant-select': '' });
      product.variants.forEach((v) => {
        const opt = el('option', {
          value: v.id,
          disabled: v.availableForSale ? null : true,
          selected: v.id === (variant && variant.id) ? 'selected' : null,
        }, v.title);
        select.appendChild(opt);
      });
      select.addEventListener('change', () => {
        this.dataset.variantId = select.value;
        const chosen = product.variants.find((v) => v.id === select.value);
        if (chosen && chosen.price) applyPrice(chosen.price);
      });
      meta.appendChild(select);
    }

    productHost.appendChild(meta);

    const actions = el('div', { class: 'rf__step-actions' });

    // One-time purchase only — the subscribe toggle was removed to avoid confusion.

    const addBtn = el('button', {
      type: 'button',
      class: 'rf__add-to-cart',
      'data-rf-add-to-cart': '',
      onclick: () => this._addToCart(),
    }, t('routine_finder.actions.add_to_cart'));
    actions.appendChild(addBtn);

    if (!isEye) {
      const seeMoreBtn = el('button', {
        type: 'button',
        class: 'rf__see-more',
        'data-rf-see-more': '',
        'aria-expanded': 'false',
        onclick: () => this._toggleAlternates(),
      }, t('routine_finder.actions.see_more'));
      actions.appendChild(seeMoreBtn);
    }

    this.appendChild(actions);
    this.appendChild(el('div', { class: 'rf__alternates', 'data-rf-alternates': '', hidden: true }));
  }

  async _addToCart() {
    if (!this.dataset.variantId) return;
    const btn = this.querySelector('[data-rf-add-to-cart]');
    if (btn && btn.dataset.busy) return;        // guard against rapid repeat clicks
    if (btn) {
      btn.dataset.busy = '1';
      btn.disabled = true;
      btn.textContent = tx('routine_finder.actions.adding', 'Adding…');
    }
    const variantNumericId = parseInt(this.dataset.variantId.replace('gid://shopify/ProductVariant/', ''), 10);
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantNumericId, quantity: 1 }),
      });
      if (!resp.ok) throw new Error(`cart/add ${resp.status}`);
      emitDataLayer('rf_step_added_to_cart', { handle: this.dataset.handle });
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      if (btn) { btn.textContent = tx('routine_finder.actions.added', '✓ Added'); btn.classList.add('rf__add-to-cart--added'); }
    } catch (e) {
      console.error('[routine-finder] add-to-cart failed', e);
      if (btn) btn.textContent = tx('routine_finder.actions.try_again', 'Try again');
    } finally {
      if (btn) setTimeout(() => {
        btn.textContent = tx('routine_finder.actions.add_to_cart', 'Add to cart');
        btn.classList.remove('rf__add-to-cart--added');
        btn.disabled = false;
        delete btn.dataset.busy;
      }, 1800);
    }
  }

  async _toggleAlternates() {
    const drawer = this.querySelector('[data-rf-alternates]');
    const btn = this.querySelector('[data-rf-see-more]');
    if (!drawer || !btn) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      drawer.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      return;
    }
    if (!drawer.dataset.loaded) {
      while (drawer.firstChild) drawer.removeChild(drawer.firstChild);
      drawer.appendChild(el('p', { class: 'rf__alternates-loading' }, t('routine_finder.state.loading')));
      // Sub-steps (1_5, 4_5) share the parent integer step's alternates pool (saho.step:N).
      const stepNumber = parseInt(this.dataset.step, 10);
      const products = await fetchAlternatesByStep(stepNumber, {
        shopDomain: this._root.shopDomain,
        storefrontToken: this._root.storefrontToken,
        locale: this._root.locale,
        country: this._root.country,
        language: this._root.locale,
      });
      while (drawer.firstChild) drawer.removeChild(drawer.firstChild);
      const currentHandle = this.dataset.handle;
      products.filter((p) => p.handle !== currentHandle).forEach((p) => {
        const button = el('button', {
          type: 'button',
          class: 'rf__alternate',
          'data-handle': p.handle,
          'data-product-id': p.id,
          onclick: () => this._swap({ handle: p.handle, product_id: p.id }),
        });
        if (p.featuredImage && p.featuredImage.url) {
          const img = el('img', { class: 'rf__alternate-img', alt: p.title });
          img.src = p.featuredImage.url;
          button.appendChild(img);
        }
        button.appendChild(el('span', { class: 'rf__alternate-title' }, p.title));
        button.appendChild(el('span', { class: 'rf__alternate-price' }, formatMoney(p.priceRange.minVariantPrice)));
        drawer.appendChild(button);
      });
      drawer.dataset.loaded = 'true';
    }
    drawer.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
  }

  async _swap(product) {
    const slot = this.dataset.slot;
    const previousHandle = this.dataset.handle;
    this.dataset.handle = product.handle;
    this.dataset.productId = product.product_id;
    this.dataset.variantId = '';
    this.dataset.sellingPlanId = '';
    this._hydrated = false;
    await this._render();
    if (this._root && this._root.state) {
      this._root.state.swapSlot(slot, { product_id: product.product_id, handle: product.handle, slot });
      saveToLocalStorage(this._root.state);
    }
    emitDataLayer('rf_step_swapped', { slot, from_product: previousHandle, to_product: product.handle });
  }
}

// ── <routine-result> ───────────────────────────────────────────────────────

class RoutineResult extends HTMLElement {
  connectedCallback() {
    this.addEventListener('rf:render-result', (e) => this._render(e.detail.state, e.detail.halfRitualSteps));
    // step cards emit rf:price-change as they hydrate / on variant change / on swap
    this.addEventListener('rf:price-change', () => this._updateTotal());
  }

  _render(state, halfRitualSteps) {
    this._state = state;
    this._halfRitualSteps = halfRitualSteps || [2, 3, 5];
    while (this.firstChild) this.removeChild(this.firstChild);

    // Signature navy-and-gold band crowning the result.
    const hero = el('div', { class: 'rf-result__hero' });
    hero.appendChild(el('span', { class: 'rf-result__hero-eyebrow' }, tx('routine_finder.result.eyebrow', 'Your personalised regimen')));
    hero.appendChild(el('h2', { class: 'rf-result__hero-title' }, tx('routine_finder.result.title', 'Your Ritual')));
    hero.appendChild(el('p', { class: 'rf-result__hero-sub' }, tx('routine_finder.result.subtitle', 'Curated for your skin, step by step.')));
    hero.appendChild(el('div', { class: 'rf-result__hero-rule' }));
    this.appendChild(hero);

    const header = el('div', { class: 'rf-result__header' });
    header.appendChild(el('button', {
      type: 'button',
      class: 'rf-result__restart',
      onclick: () => this.dispatchEvent(new CustomEvent('rf:restart', { bubbles: true })),
    }, '⟲ ' + t('routine_finder.nav.restart')));
    header.appendChild(el('button', {
      type: 'button',
      class: 'rf-result__view-answers',
      onclick: () => this._answersModal.open(this._state.answers),
    }, '👁 ' + t('routine_finder.actions.view_answers')));
    this.appendChild(header);

    const stepsHost = el('div', { class: 'rf-result__steps' });
    state.recommendation.steps.forEach((group) => {
      const groupEl = el('div', { class: 'rf-result__group', 'data-rf-group': String(group.step) });
      groupEl.appendChild(el('h3', { class: 'rf-result__group-label' }, t(`routine_finder.step.${group.step}.label`)));
      const cards = el('div', { class: 'rf-result__group-cards' });
      group.products.forEach((p) => {
        const eff = state.effectiveSlotProduct(p.slot) || p;
        cards.appendChild(this._buildStepCard(group.step, eff));
      });
      groupEl.appendChild(cards);
      stepsHost.appendChild(groupEl);
    });
    this.appendChild(stepsHost);

    const addonHost = el('div', { class: 'rf-result__addon' });
    const eyeCard = this._buildStepCard('eye', { ...state.recommendation.eyeAddon, slot: 'eye' });
    addonHost.appendChild(eyeCard);
    this.appendChild(addonHost);

    const totalRow = el('div', { class: 'rf-result__total' });
    totalRow.appendChild(el('span', { class: 'rf-result__total-label' }, tx('routine_finder.result.total', 'Routine total')));
    totalRow.appendChild(el('span', { class: 'rf-result__total-amount', 'data-rf-total-amount': '' }, ''));
    this.appendChild(totalRow);

    const footer = el('div', { class: 'rf-result__footer' });
    footer.appendChild(el('button', {
      type: 'button',
      class: 'rf-result__add-routine',
      onclick: () => this._addRoutineToCart(),
    }, t('routine_finder.actions.add_routine_to_cart')));
    footer.appendChild(el('button', {
      type: 'button',
      class: 'rf-result__save',
      onclick: () => this._saveForm.open(this._state, this.closest('routine-finder')),
    }, t('routine_finder.actions.save_for_later')));
    this.appendChild(footer);

    this._saveForm = document.createElement('routine-save-form');
    this._saveForm.hidden = true;
    this.appendChild(this._saveForm);

    this._answersModal = document.createElement('routine-answers-modal');
    this._answersModal.hidden = true;
    this.appendChild(this._answersModal);

    this._applyRitualFilter();
    this._updateTotal();
  }

  // Sum the displayed price of every visible card (respects half-ritual + eye add-on).
  _updateTotal() {
    const cards = Array.from(this.querySelectorAll('routine-step-card')).filter((c) => {
      const group = c.closest('[data-rf-group]');
      return !c.hidden && !(group && group.hidden);
    });
    let sum = 0;
    let currency = 'EUR';
    let counted = 0;
    cards.forEach((c) => {
      const a = parseFloat(c.dataset.priceAmount || '');
      if (!isNaN(a)) { sum += a; counted += 1; }
      if (c.dataset.priceCurrency) currency = c.dataset.priceCurrency;
    });
    const amountEl = this.querySelector('[data-rf-total-amount]');
    if (amountEl) amountEl.textContent = counted ? formatMoney({ amount: String(sum), currencyCode: currency }) : '';
  }

  _buildStepCard(stepNumber, product) {
    const card = document.createElement('routine-step-card');
    card.className = 'rf__step';
    card.dataset.step = String(stepNumber);
    card.dataset.slot = product.slot || String(stepNumber);
    card.dataset.handle = product.handle;
    card.dataset.productId = product.product_id;
    return card;
  }

  _applyRitualFilter() {
    const ritual = this._state.answers.ritual || 'full';
    this.querySelectorAll('[data-rf-group]').forEach((g) => {
      const step = parseInt(g.dataset.rfGroup, 10);
      g.hidden = ritual !== 'full' && !this._halfRitualSteps.includes(step);
    });
  }

  async _addRoutineToCart() {
    const btn = this.querySelector('.rf-result__add-routine');
    if (btn && btn.dataset.busy) return;
    const visibleCards = Array.from(this.querySelectorAll('routine-step-card')).filter((c) => {
      const group = c.closest('[data-rf-group]');
      return !c.hidden && !(group && group.hidden);
    });
    const items = [];
    visibleCards.forEach((card) => {
      const variantId = card.dataset.variantId;
      if (!variantId) return;
      items.push({ id: parseInt(variantId.replace('gid://shopify/ProductVariant/', ''), 10), quantity: 1 });
    });
    if (!items.length) return;
    if (btn) { btn.dataset.busy = '1'; btn.disabled = true; btn.textContent = tx('routine_finder.actions.adding', 'Adding…'); }
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!resp.ok) throw new Error(`cart/add ${resp.status}`);
      emitDataLayer('rf_routine_added_to_cart', { handles: visibleCards.map((c) => c.dataset.handle) });
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      if (btn) { btn.textContent = tx('routine_finder.actions.added', '✓ Added'); btn.classList.add('rf-result__add-routine--added'); }
    } catch (e) {
      console.error('[routine-finder] add-routine failed', e);
      if (btn) btn.textContent = tx('routine_finder.actions.try_again', 'Try again');
      const footer = this.querySelector('.rf-result__footer');
      if (footer) {
        let toast = footer.querySelector('.rf-result__error');
        if (!toast) {
          toast = el('p', { class: 'rf-result__error' }, t('routine_finder.error.cart'));
          footer.appendChild(toast);
        } else {
          toast.textContent = t('routine_finder.error.cart');
        }
      }
    } finally {
      if (btn) setTimeout(() => {
        btn.textContent = t('routine_finder.actions.add_routine_to_cart');
        btn.classList.remove('rf-result__add-routine--added');
        btn.disabled = false;
        delete btn.dataset.busy;
      }, 2200);
    }
  }
}

// ── <routine-save-form> ────────────────────────────────────────────────────

class RoutineSaveForm extends HTMLElement {
  open(state, root) {
    this._state = state;
    this._root = root;
    this.hidden = false;
    if (!this._rendered) this._build();
  }

  _build() {
    while (this.firstChild) this.removeChild(this.firstChild);

    const form = el('form', { class: 'rf-save', 'data-rf-save-form-el': '', onsubmit: (e) => this._submit(e) });

    const emailWrap = el('label', { class: 'rf-save__field' });
    emailWrap.appendChild(el('span', { class: 'rf-save__label' }, t('routine_finder.save.email_label')));
    const emailInput = el('input', { type: 'email', required: true, name: 'email', autocomplete: 'email' });
    emailWrap.appendChild(emailInput);
    form.appendChild(emailWrap);

    const optInWrap = el('label', { class: 'rf-save__optin' });
    optInWrap.appendChild(el('input', { type: 'checkbox', name: 'opt_in', checked: true }));
    optInWrap.appendChild(el('span', null, t('routine_finder.save.opt_in')));
    form.appendChild(optInWrap);

    form.appendChild(el('button', { type: 'submit', class: 'rf-save__submit' }, t('routine_finder.save.submit')));
    form.appendChild(el('p', { class: 'rf-save__status', 'data-rf-save-status': '', hidden: true }));

    this.appendChild(form);
    this._rendered = true;
  }

  async _submit(e) {
    e.preventDefault();
    const form = e.target;
    const email = form.email.value.trim();
    const optIn = form.opt_in.checked;
    if (!email) return;
    const status = this.querySelector('[data-rf-save-status]');
    status.hidden = false;
    status.textContent = t('routine_finder.save.in_progress');

    const stepProducts = [];
    this._state.recommendation.steps.forEach((g) => {
      g.products.forEach((p) => {
        const eff = this._state.swaps[p.slot] || p;
        stepProducts.push({ step: g.step, slot: p.slot, handle: eff.handle, product_id: eff.product_id, sku: eff.sku });
      });
    });

    try {
      const result = await saveRoutine({
        email,
        optIn,
        klaviyoCompanyId: this._root.klaviyoCompanyId,
        klaviyoListId: this._root.klaviyoListId,
        routineUrl: `${window.location.origin}${window.location.pathname}?routine=${encodeToken(this._state.answers, this._state.swaps)}`,
        answers: this._state.answers,
        steps: stepProducts,
        eyeAddon: this._state.recommendation.eyeAddon,
      });
      if (result.klaviyo === 'ok') {
        status.textContent = t('routine_finder.save.success');
        this._state.savedAt = new Date().toISOString();
        saveToLocalStorage(this._state);
        emitDataLayer('rf_routine_saved', { ...this._state.answers });
      } else {
        status.textContent = t('routine_finder.save.error');
      }
    } catch (err) {
      console.error('[routine-finder] save failed', err);
      status.textContent = t('routine_finder.save.error');
    }
  }
}

// ── <routine-answers-modal> ────────────────────────────────────────────────

class RoutineAnswersModal extends HTMLElement {
  open(answers) {
    this._answers = answers;
    this._build();
    this.hidden = false;
  }

  _build() {
    while (this.firstChild) this.removeChild(this.firstChild);

    const dialog = el('div', {
      class: 'rf-answers',
      role: 'dialog',
      'aria-modal': 'true',
      onclick: (e) => { if (e.target === dialog) this.hidden = true; },
    });
    const panel = el('div', { class: 'rf-answers__panel' });
    panel.appendChild(el('button', {
      type: 'button',
      class: 'rf-answers__close',
      'aria-label': t('routine_finder.answers.close'),
      onclick: () => { this.hidden = true; },
    }, '×'));
    panel.appendChild(el('h2', { class: 'rf-answers__title' }, t('routine_finder.answers.title')));

    const list = el('ul', { class: 'rf-answers__list' });
    const order = [
      { key: 'age', q: 1 },
      { key: 'ritual', q: 2 },
      { key: 'skin', q: 3 },
      { key: 'benefit', q: 4 },
      { key: 'eye', q: 5 },
    ];
    order.forEach(({ key, q }) => {
      const value = this._answers[key] || '';
      const promptText = t(`routine_finder.q${q}.prompt`);
      const labelText = t(`routine_finder.q${q}.option.${value}.label`);
      const row = el('li', { class: 'rf-answers__row' });
      row.appendChild(el('span', { class: 'rf-answers__prompt' }, promptText));
      row.appendChild(el('span', { class: 'rf-answers__value' }, labelText));
      row.appendChild(el('a', {
        class: 'rf-answers__edit',
        href: `#q${q}`,
        onclick: () => { this.hidden = true; },
      }, t('routine_finder.answers.edit')));
      list.appendChild(row);
    });
    panel.appendChild(list);
    dialog.appendChild(panel);
    this.appendChild(dialog);
  }
}

// ── Custom-element registration (gated for Node tests) ─────────────────────

if (typeof window !== 'undefined' && window.customElements) {
  // Define children before the <routine-finder> orchestrator. On a fresh load that
  // lands directly on #result (browser Back, refresh, or a shared #result link),
  // <routine-finder>'s connectedCallback renders the result by dispatching
  // rf:render-result. If the parent is defined first, that event fires before
  // <routine-result> has been upgraded and attached its listener, so the result
  // renders empty. Registering the parent LAST guarantees the child is listening.
  const defs = [
    ['routine-question', RoutineQuestion],
    ['routine-step-card', RoutineStepCard],
    ['routine-result', RoutineResult],
    ['routine-save-form', RoutineSaveForm],
    ['routine-answers-modal', RoutineAnswersModal],
    ['routine-finder', RoutineFinderRoot],
  ];
  for (const [name, ctor] of defs) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  }
}
